import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from "../api";
import "./login.css";

function ResetPassword() {
  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const navigate = useNavigate();

  const strength = useMemo(() => {
    if (!newPassword) return "";
    let score = 0;
    if (newPassword.length >= 6) score++;
    if (/[A-Z]/.test(newPassword)) score++;
    if (/[0-9]/.test(newPassword) || /[^A-Za-z0-9]/.test(newPassword)) score++;

    if (score <= 1) return "weak";
    if (score === 2) return "medium";
    return "strong";
  }, [newPassword]);

  // Auto focus on step change
  useEffect(() => {
    if (step === 1 && usernameRef.current) {
      usernameRef.current.focus();
    }
    if (step === 2 && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [step]);

  const checkUsername = async (e) => {
    if (e) e.preventDefault();

    setError("");
    setSuccess("");

    const trimmedUser = username.trim();
    if (!trimmedUser) {
      setError("Please enter your username");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/check_username.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: trimmedUser })
      });

      const data = await res.json();

      if (data && data.exists) {
        setStep(2);
      } else {
        setError(data?.message || "Username not found in system");
      }
    } catch (err) {
      console.error("Check username error:", err);
      setError("Network connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (e) => {
    if (e) e.preventDefault();

    setError("");
    setSuccess("");

    if (newPassword.length < 4) {
      setError("Password must be at least 4 characters");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/reset_password.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          newPassword,
          change: true
        })
      });

      const data = await res.json();

      if (data && data.success) {
        setSuccess("Password reset successful! You can now log in.");
        setStep(3);
      } else {
        setError(data?.message || "Password reset failed.");
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setError("Network connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Efficient keyboard handler without unnecessary state re-renders (Fixes INP)
  const handlePasswordKey = (e) => {
    if (e.getModifierState) {
      const caps = e.getModifierState("CapsLock");
      setCapsLockOn((prev) => (prev !== caps ? caps : prev));
    }
  };

  return (
    <div
      className="login-bg"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/login_wallpaper.webp)`
      }}
    >
      <div className="login-overlay" />

      <div className="login-box">
        <h2 className="login-title">Reset Password</h2>

        {/* Message slot with reserved height to eliminate CLS */}
        <div className="login-message-slot">
          {error && <p className="login-error">{error}</p>}
          {success && <p className="login-success">{success}</p>}
        </div>

        {/* STEP 1: Enter Username */}
        {step === 1 && (
          <form onSubmit={checkUsername}>
            <input
              type="text"
              placeholder="Enter Username"
              className="login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              ref={usernameRef}
              autoComplete="username"
            />

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
            >
              {loading && <span className="btn-spinner" />}
              <span>{loading ? "Checking..." : "Continue"}</span>
            </button>

            <p
              className="forgot-link"
              onClick={() => navigate("/admin/login")}
              style={{ marginTop: "14px" }}
            >
              Back to Login
            </p>
          </form>
        )}

        {/* STEP 2: Enter New Password */}
        {step === 2 && (
          <form onSubmit={updatePassword}>
            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter New Password"
                className="login-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={handlePasswordKey}
                onKeyUp={handlePasswordKey}
                ref={passwordRef}
                autoComplete="new-password"
              />

              <i
                className={`password-icon fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                onClick={() => setShowPassword((prev) => !prev)}
                title={showPassword ? "Hide password" : "Show password"}
              ></i>
            </div>

            {capsLockOn && <div className="caps-warning">Caps Lock is ON</div>}

            {strength && (
              <div className="strength-container">
                <span className="strength-label">
                  Strength: <strong className={strength}>{strength}</strong>
                </span>
                <div className="strength-bar-bg">
                  <div className={`strength-bar-fill ${strength}`} />
                </div>
              </div>
            )}

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
              style={{ marginTop: "10px" }}
            >
              {loading && <span className="btn-spinner" />}
              <span>{loading ? "Updating..." : "Reset Password"}</span>
            </button>

            <p
              className="forgot-link"
              onClick={() => navigate("/admin/login")}
              style={{ marginTop: "14px" }}
            >
              Cancel
            </p>
          </form>
        )}

        {/* STEP 3: Done */}
        {step === 3 && (
          <div className="reset-done-box">
            <div className="reset-done-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <p className="reset-done-text">Your password has been successfully updated!</p>
            <button
              type="button"
              className="login-btn"
              onClick={() => navigate("/admin/login")}
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;